import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/HPEJuniperNetworking.css";
import hpeJuniperLogo from "../../assets/images/partners/HPEJuniperNetworking.png";

function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const aiCells = [
  { icon: "🤖", title: "Marvis AI Network Assistant", desc: "Juniper Marvis is the industry's most capable AI network assistant — answering operational questions in natural language, identifying root causes from SLE (Service Level Experience) data, and resolving issues proactively before users notice." },
  { icon: "📊", title: "Service Level Experience (SLE)", desc: "Mist's SLE framework measures Wi-Fi performance from the client's perspective — tracking connection success rates, throughput, roaming performance, and coverage — with specific failure attribution replacing generic 'network is slow' reports." },
  { icon: "🔭", title: "Proactive Root Cause Analysis", desc: "Marvis analyzes SLE data continuously — detecting anomalies, correlating events across the network, and surfacing root causes with specific remediation recommendations before end users submit help desk tickets." },
  { icon: "🎛️", title: "RF Optimization AI", desc: "Juniper Mist's AI continuously optimizes Wi-Fi RF environments — adjusting transmit power, channel selection, and client steering based on real-time client density, interference patterns, and throughput measurements." },
  { icon: "🔗", title: "WAN Assurance AI", desc: "AI-powered monitoring for Juniper SD-WAN — tracking application performance across WAN paths, predicting circuit degradation, and automatically steering traffic before performance impacts reach users." },
  { icon: "🏗️", title: "Network Digital Twin", desc: "Mist creates a digital twin of your network environment — enabling AI-driven simulation of configuration changes before production deployment, eliminating change-window risk from network modifications." },
];

const campusTiers = [
  { dotCls: "blue", icon: "🌐", name: "WAN / SD-WAN Edge", desc: "Juniper SRX Series with Session Smart routing — intelligent path selection, application-aware WAN optimization, and zero-trust WAN security" },
  { dotCls: "blue", icon: "⚡", name: "EX9200 Core Distribution", desc: "High-density 40G/100G campus core with EVPN-VXLAN fabric — providing L3 redundancy, multi-tenancy, and campus-wide QoS policy enforcement" },
  { dotCls: "teal", icon: "🏢", name: "EX4100 / EX4650 Access", desc: "PoE++ access switching with 90W per-port capability — powering next-gen Wi-Fi 6E APs, IP cameras, and connected meeting room devices" },
  { dotCls: "teal", icon: "📡", name: "Mist AP43 / AP63 Wi-Fi 6E", desc: "Juniper Mist Wi-Fi 6E access points with Virtual BLE for location services — managed entirely through Mist Cloud with Marvis AI analytics" },
  { dotCls: "navy", icon: "📊", name: "Mist Cloud Management", desc: "Full-stack cloud management for all tiers — unifying switches, APs, and WAN edges with AI-driven analytics from a single dashboard" },
];

const campusFeatures = [
  "Automated campus switch configuration through Mist Cloud zero-touch provisioning",
  "Dynamic VLAN assignment via Mist Access Assurance (NAC) without separate appliances",
  "Virtual BLE location services for asset tracking and indoor navigation",
  "AI-driven troubleshooting reducing MTTR from hours to minutes",
  "Integration with ServiceNow, Aruba ClearPass, and Okta for identity-aware access",
];

const dcFabric = [
  { icon: "🔄", title: "EVPN-VXLAN Fabric Architecture", desc: "Juniper QFX Series switches implement EVPN-VXLAN leaf-spine architecture — providing Layer 2 extension across Layer 3 fabrics, enabling VM mobility and multi-tenancy without spanning tree complexity." },
  { icon: "⚡", title: "QFX10000 Spine Switches", desc: "High-density 100G/400G spine switches with hardware-accelerated VXLAN — supporting up to 3.6 Tbps aggregate throughput for hyperscale data center fabric and cloud interconnect." },
  { icon: "🌐", title: "QFX5120 Leaf Switches", desc: "48-port 25G server-facing leaf switches with 100G uplinks — ultra-low latency for HPC, AI/ML training clusters, and latency-sensitive financial applications." },
  { icon: "🤖", title: "AI-Driven Fabric Management", desc: "Apstra intent-based networking for data center fabric — defining desired network state in plain language and Apstra automatically configures all fabric devices to achieve it continuously." },
  { icon: "🔐", title: "Micro-Segmentation", desc: "Juniper Connected Security micro-segmentation policies applied to individual workloads — preventing lateral movement within the data center fabric without requiring hardware firewall appliances." },
];

const wirelessSpecs = [
  { val: "Wi-Fi 6E", label: "Latest Generation" },
  { val: "7.3 Gbps", label: "AP43 Throughput" },
  { val: "6 GHz", label: "Tri-Band Support" },
  { val: "BLE+Wi-Fi", label: "Dual-Radio Design" },
];

const wirelessFeatures = [
  "Mist AI delivers per-client Wi-Fi performance scoring updated every 5 minutes",
  "Virtual BLE enables 1-3m indoor location accuracy without dedicated hardware",
  "Automated RF interference detection and channel reassignment by Marvis AI",
  "Zero-touch AP provisioning through Mist Cloud — APs configure automatically on first boot",
  "Guest Wi-Fi with social authentication, SMS verification, and bandwidth throttling",
  "PSK-based multi-tenant access without 802.1X certificate infrastructure",
];

const aiopsCaps = [
  { icon: "🎙️", title: "Natural Language Queries", desc: "Ask Marvis 'Why is wireless slow in Building 3?' and receive specific root cause analysis with remediation steps — eliminating manual log analysis and specialized network expertise requirements." },
  { icon: "📈", title: "Anomaly Detection", desc: "Marvis continuously compares current network behavior to historical baselines — detecting performance degradation before users notice and identifying the specific causal factor from the SLE data set." },
  { icon: "🔧", title: "Automated Remediation", desc: "Marvis Action Dashboard provides one-click or automated remediation for common issues — bouncing ports, clearing ARP caches, and optimizing RF parameters without human CLI interaction." },
  { icon: "📊", title: "Helpdesk Integration", desc: "Marvis provides context-rich troubleshooting information directly in ServiceNow tickets — reducing helpdesk mean time to resolution by supplying network performance data automatically with each wireless complaint." },
];

const deploySteps = [
  { num: "01", icon: "🔍", title: "Discovery & Assessment", desc: "Complete discovery of existing network topology, traffic baselines, VLAN structure, and security policy — establishing the baseline for Juniper Mist migration or greenfield design." },
  { num: "02", icon: "🗺️", title: "Architecture Design", desc: "Juniper reference architecture tailored to your campus layout, user density, and application requirements — with EVPN fabric design, SLE target definition, and Marvis baseline configuration." },
  { num: "03", icon: "☁️", title: "Mist Cloud Onboarding", desc: "Mist organizational hierarchy setup, site configuration, network policy definition, and AP group templates — all configured before hardware arrives on-site through zero-touch provisioning." },
  { num: "04", icon: "⚡", title: "Zero-Touch Deployment", desc: "Juniper switches and APs configure automatically from Mist Cloud on first boot — minimal on-site technician time required for physical installation without CLI configuration work." },
];

const industries = [
  { icon: "🎓", name: "Higher Education", desc: "High-density campus Wi-Fi for lecture halls and student residences, Virtual BLE indoor navigation for campus apps, and Mist AI reducing network operations team workload." },
  { icon: "🏥", name: "Healthcare", desc: "HIPAA-compliant wireless with clinical IoT segmentation, location-based asset tracking through Virtual BLE, and AI-driven troubleshooting reducing impact on patient care environments." },
  { icon: "🏢", name: "Enterprise Campus", desc: "Multi-building campus switching and wireless fabric with Marvis AI reducing MTTR, Mist Access Assurance for zero-trust NAC, and unified cloud management." },
  { icon: "🏭", name: "Manufacturing", desc: "Industrial wireless for factory floors, warehouse RF optimization for handheld devices, and secure OT-adjacent wireless infrastructure with IoT device segmentation." },
  { icon: "🏛️", name: "Government", desc: "FedRAMP-authorized Juniper Mist for federal and state agencies — with FIPS 140-2 compliant encryption and security configuration templates for government compliance requirements." },
  { icon: "🏦", name: "Financial Services", desc: "Ultra-low latency campus fabric for trading floor applications, zero-trust network access, and Mist AI-driven wireless performance assurance for regulated environments." },
  { icon: "🏨", name: "Hospitality", desc: "Guest Wi-Fi with seamless roaming across large hotel properties, PMS integration for automatic guest network provisioning, and per-room bandwidth policy enforcement." },
  { icon: "⚡", name: "Utilities & Energy", desc: "Campus networking for utility offices and operations centers — with high-availability fabric design, redundancy engineering, and OT-aligned security segmentation." },
];

const faqData = [
  { q: "What is Juniper Mist and how does it differ from traditional Wi-Fi management systems?", a: "Juniper Mist is a cloud-native wireless management platform built around AI operations rather than traditional configuration management. Traditional Wi-Fi management platforms (like legacy Cisco WLC or Aruba Mobility Controller) are primarily configuration tools — you push settings to APs and monitor basic health metrics. Juniper Mist goes significantly further: Marvis AI continuously analyzes network telemetry from every Mist-managed AP and switch, measuring service level experience from the client's perspective, detecting anomalies compared to historical baselines, identifying root causes when performance degrades, and surfacing specific remediation recommendations. A network administrator running Mist asks Marvis 'why are users in Conference Room 4 having trouble connecting?' and receives a specific answer ('AP43-B is experiencing co-channel interference on channel 36 from a neighboring AP — recommend channel change to 100') rather than reviewing pages of logs manually. Mist also includes Virtual BLE for meter-level indoor location services using Bluetooth Low Energy radios built into every Mist AP — enabling indoor navigation, asset tracking, and proximity-based services without separate hardware." },
  { q: "What is the HPE acquisition of Juniper Networks and how does it affect products?", a: "HPE (Hewlett Packard Enterprise) completed its acquisition of Juniper Networks in 2024 — combining HPE's networking portfolio (primarily Aruba Networks) with Juniper's networking and AI operations portfolio (Juniper Mist, EX Series switching, QFX data center, SRX security, and AI-driven WAN). The combined portfolio creates one of the largest enterprise networking companies globally, with complementary product lines across campus, data center, and WAN. Currently, both Juniper and Aruba products continue as distinct offerings with their respective management platforms (Juniper Mist and Aruba Central). The combined organization provides a comprehensive networking portfolio that ALG Data Guard can draw from — recommending Juniper Mist for organizations prioritizing AI operations and wireless SLE analytics, Aruba for organizations deeply integrated with HPE infrastructure and GreenLake cloud services, or combinations where each technology fits different use cases within the same customer environment." },
  { q: "What is Juniper Marvis and why is it significant for network operations?", a: "Juniper Marvis is an AI-powered network assistant that fundamentally changes how network operations teams work. Traditional network operations require engineers to manually analyze logs, run CLI commands on individual devices, correlate events across systems, and apply experience to identify root causes — a process requiring significant expertise and time. Marvis automates this analytical work: it continuously processes all telemetry from Mist-managed Wi-Fi, wired switches, and SD-WAN — comparing current performance against historical baselines, identifying statistical anomalies, correlating related events, and surfacing root causes with specific remediation recommendations. Marvis operates through a natural language interface — team members type questions ('why is wireless slow in the east wing?') and Marvis responds with specific diagnoses rather than requiring log analysis expertise. Marvis Action Dashboard identifies common proactive issues (rogue APs, DHCP problems, channel conflicts, firmware inconsistencies) with one-click remediation — enabling even junior IT staff to resolve issues that previously required senior network engineers. Organizations using Marvis typically reduce wireless-related helpdesk tickets by 40-60% and MTTR by similar margins." },
  { q: "What is Juniper EVPN-VXLAN and when is it appropriate for campus or data center environments?", a: "EVPN-VXLAN (Ethernet VPN over Virtual Extensible LAN) is a modern network fabric technology replacing traditional spanning-tree-based Layer 2 networks in both campus and data center environments. Traditional networks use spanning tree protocol (STP) to prevent loops in Layer 2 networks — but STP blocks redundant links, wastes bandwidth, and creates complex failure scenarios. EVPN-VXLAN provides the same Layer 2 connectivity (same IP subnet, same VLAN) across a routed Layer 3 underlay infrastructure — enabling all links to be active simultaneously, dramatically improving bandwidth utilization and network resilience. In data centers, EVPN-VXLAN enables leaf-spine fabric architecture with workload mobility, multi-tenancy, and micro-segmentation at scale. In large campus environments (universities, corporate headquarters), EVPN-VXLAN replaces complex spanning-tree topologies with a simpler, more resilient routed underlay that carries all VLANs as VXLAN overlays. ALG Data Guard designs EVPN-VXLAN deployments on Juniper QFX and EX platforms, implementing appropriate underlay routing (OSPF or eBGP) and overlay EVPN configuration." },
  { q: "How does ALG Data Guard deploy and manage Juniper Mist environments?", a: "ALG Data Guard provides complete Juniper Mist deployment services: initial discovery assessing your existing network environment, traffic baselines, VLAN requirements, and security policies. Mist organizational design — creating the site and floor hierarchy, network policy templates, and AP group configurations in Mist Cloud before any hardware arrives. Zero-touch AP provisioning — Mist APs are claimed in Mist Cloud and automatically receive configuration when connected to network power. Wired switch onboarding — Juniper EX switches are onboarded to Mist through the same cloud interface, providing unified wired and wireless management. Marvis baseline configuration — establishing SLE targets appropriate for your environment and enabling proactive issue detection. Post-deployment managed services include Mist cloud administration, firmware update coordination, SLE performance monitoring with monthly reporting, Marvis action response, and expansion support as you add new sites or buildings. We also provide integration services connecting Mist to ServiceNow for helpdesk ticket automation, Okta or Azure AD for identity-aware network access, and CMDB systems for network inventory management." },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);
const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

export default function HPEJuniperNetworking() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [metricsRef, metricsInView] = useInView(0.3);
  const c1 = useCounter(60, 2200, metricsInView);
  const c2 = useCounter(50, 2000, metricsInView);
  const c3 = useCounter(100, 2200, metricsInView);
  const c4 = useCounter(40, 2000, metricsInView);

  return (
    <div className="hj-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band" style={{ background:"#0a0f1a", borderBottom:"1px solid rgba(0,116,217,0.2)" }}>
        <div className="pd-identity-inner">
          <img src={hpeJuniperLogo} alt="HPE Juniper Networking" className="pd-partner-logo" />
          <div className="pd-identity-divider" style={{ background:"rgba(255,255,255,0.12)" }} aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow" style={{ color:"rgba(255,255,255,0.6)", background:"rgba(0,116,217,0.12)", borderColor:"rgba(0,116,217,0.25)" }}>
              <span className="pd-live-dot" style={{ background:"#01A982" }} />
              AI-Native Enterprise Networking Partner
            </div>
            <h1 className="pd-identity-name" style={{ color:"#fff" }}>ALG Data Guard × HPE Juniper Networking</h1>
            <p className="pd-identity-desc" style={{ color:"rgba(255,255,255,0.65)" }}>
              Juniper Networks (an HPE company) is the inventor of AI-native networking — delivering enterprise Wi-Fi, campus switching, data center fabric, SD-WAN, and network security through the Juniper Mist cloud platform powered by Marvis AI. Where traditional network management requires engineers to manually diagnose issues through log analysis, Juniper Mist's Marvis AI continuously measures Service Level Experience from the user's perspective — proactively identifying root causes and surfacing remediation recommendations before end users notice problems. ALG Data Guard is an authorized Juniper partner providing design, deployment, and managed services for the complete Juniper Mist and switching portfolio.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="hj-hero hj-blueprint">
        <div className="hj-hero__grad" aria-hidden="true" />
        {/* Floating architecture diagram */}
        <div className="hj-hero__arch" aria-hidden="true">
          {[
            { style:{top:"50%",left:"50%",width:"80px",height:"80px",transform:"translate(-50%,-50%)"}, cls:"hj-arch-node--core", label:"Mist\nCloud" },
            { style:{top:"8%",left:"38%",width:"70px",height:"36px"}, cls:"hj-arch-node--green", label:"WAN Edge" },
            { style:{top:"8%",left:"62%",width:"70px",height:"36px"}, cls:"hj-arch-node--blue", label:"SRX FW" },
            { style:{top:"80%",left:"25%",width:"70px",height:"36px"}, cls:"hj-arch-node--blue", label:"EX Core" },
            { style:{top:"80%",left:"60%",width:"70px",height:"36px"}, cls:"hj-arch-node--green", label:"Mist AP" },
            { style:{top:"42%",left:"4%",width:"60px",height:"36px"}, cls:"hj-arch-node--blue", label:"QFX DC" },
            { style:{top:"42%",left:"85%",width:"60px",height:"36px"}, cls:"hj-arch-node--green", label:"Marvis" },
          ].map((n, i) => (
            <div key={i} className={`hj-arch-node ${n.cls}`} style={{ position:"absolute", ...n.style, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.62rem", letterSpacing:"0.03em", textAlign:"center" }}>
              {n.label}
            </div>
          ))}
        </div>

        <div className="hj-hero__inner">
          <div>
            <div className="hj-hero__badge">
              <span className="hj-hero__badge-item hj-hero__badge-blue">Juniper Networks</span>
              <span className="hj-hero__badge-item hj-hero__badge-green">HPE Company</span>
            </div>
            <h2 className="hj-display hj-hero__title" style={{ color:"#fff", marginBottom:"1rem" }}>
              AI-Native<br /><span style={{ color:"var(--hj-green-light)" }}>Networking.</span><br />Intelligent<br />Operations.
            </h2>
            <p className="hj-hero__body">
              Juniper Mist with Marvis AI transforms network operations from reactive troubleshooting to proactive experience management — measuring wireless and wired performance from the user's perspective, not the network's. ALG Data Guard deploys and manages Juniper Mist for organizations that need campus networking to operate at enterprise scale without enterprise operational complexity.
            </p>
            <div className="hj-hero__actions">
              <button className="hj-btn hj-btn--green" onClick={() => navigate("/contact")}>
                Design My AI Network <ArrowIcon />
              </button>
              <button className="hj-btn hj-btn--ghost" onClick={() => navigate("/contact")}>
                Mist Assessment
              </button>
            </div>
            <div className="hj-hero__kpis">
              <div className="hj-hero__kpi">
                <div className="hj-hero__kpi-val">Marvis</div>
                <div className="hj-hero__kpi-label">AI Operations</div>
              </div>
              <div className="hj-hero__kpi">
                <div className="hj-hero__kpi-val hj-hero__kpi-val--green">Wi-Fi 6E</div>
                <div className="hj-hero__kpi-label">Latest Generation</div>
              </div>
              <div className="hj-hero__kpi">
                <div className="hj-hero__kpi-val">Partner</div>
                <div className="hj-hero__kpi-label">ALG Data Guard</div>
              </div>
            </div>
          </div>
          {/* Spacer for arch diagram */}
          <div />
        </div>
      </section>

      {/* ── AI Native Networking ── */}
      <section className="hj-ai">
        <div className="hj-shell">
          <div className="hj-ai__head">
            <div className="hj-tag hj-tag--green" style={{ margin:"0 auto 1rem" }}><span className="hj-dot" /> AI-Native Networking</div>
            <h2 className="hj-title">Marvis AI — The Network That Thinks, So You Don't Have To</h2>
            <p className="hj-sub" style={{ margin:"0 auto", maxWidth:"640px" }}>Juniper Mist is the only enterprise networking platform built around AI as the operating model rather than an add-on — continuously measuring, analyzing, and remediating network performance issues without requiring engineer intervention.</p>
          </div>
          <div className="hj-ai__grid">
            {aiCells.map(cell => (
              <div key={cell.title} className="hj-ai-cell">
                <span className="hj-ai-cell__icon">{cell.icon}</span>
                <h3 className="hj-ai-cell__title">{cell.title}</h3>
                <p className="hj-ai-cell__desc">{cell.desc}</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign:"center", marginTop:"3rem", position:"relative", zIndex:2 }}>
            <button className="hj-btn hj-btn--green" onClick={() => navigate("/contact")}>
              Deploy Juniper Mist <ArrowIcon />
            </button>
          </div>
        </div>
      </section>

      {/* ── Campus Architecture ── */}
      <section className="hj-campus">
        <div className="hj-shell">
          <div className="hj-campus__inner">
            <div>
              <div className="hj-tag hj-tag--light-blue"><span className="hj-dot" /> Campus Architecture</div>
              <h2 className="hj-title hj-campus__title" style={{ color:"var(--hj-text)" }}>Full-Stack Campus Fabric — Switching to Wireless to WAN</h2>
              <p className="hj-campus__body">
                Juniper's campus networking portfolio covers every infrastructure layer — from Juniper SRX WAN edge through EX Series distribution and access switching through Mist Wi-Fi 6E access points — all managed from a single Mist Cloud dashboard with Marvis AI analytics spanning the entire stack.
              </p>
              <p className="hj-campus__body">
                ALG Data Guard designs Juniper campus architectures using Juniper's validated reference designs — ensuring EVPN-VXLAN underlay for scalable multi-VLAN campus fabrics, Mist Access Assurance for cloud-native NAC without appliances, and Marvis AI operating the entire campus from a single cloud platform.
              </p>
              <div className="hj-campus__features">
                {campusFeatures.map(f => <div key={f} className="hj-campus-feat">{f}</div>)}
              </div>
              <button className="hj-btn hj-btn--dark-blue" onClick={() => navigate("/contact")}>
                Design My Campus <ArrowIcon />
              </button>
            </div>
            <div className="hj-campus__diagram">
              {campusTiers.map(tier => (
                <div key={tier.name} className="hj-campus-tier">
                  <div className={`hj-campus-tier__dot hj-campus-tier__dot--${tier.dotCls}`} />
                  <span className="hj-campus-tier__icon">{tier.icon}</span>
                  <div>
                    <div className="hj-campus-tier__name">{tier.name}</div>
                    <p className="hj-campus-tier__desc">{tier.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Data Center Fabric ── */}
      <section className="hj-dc">
        <div className="hj-shell">
          <div className="hj-dc__inner">
            <div>
              <div className="hj-tag hj-tag--blue"><span className="hj-dot" /> Data Center Fabric</div>
              <h2 className="hj-title hj-dc__title">QFX Leaf-Spine — Fabric for the Hyperscale Era</h2>
              <p className="hj-dc__body">
                Juniper QFX Series data center switches power leaf-spine EVPN-VXLAN fabrics for enterprise data centers, private cloud environments, and HPC clusters requiring ultra-low latency and multi-hundred-gigabit aggregate throughput. Juniper Apstra adds intent-based management — defining desired fabric state declaratively and Apstra enforces it continuously across all QFX nodes.
              </p>
              <p className="hj-dc__body">
                ALG Data Guard designs Juniper data center fabric deployments with appropriate leaf-spine topology, EVPN-VXLAN overlay design, micro-segmentation policy, and Apstra automation — delivering the agility of cloud-native infrastructure in your on-premises data center environment.
              </p>
              <div className="hj-dc__fabric">
                {dcFabric.map(item => (
                  <div key={item.title} className="hj-dc-layer">
                    <span className="hj-dc-layer__icon">{item.icon}</span>
                    <div>
                      <div className="hj-dc-layer__title">{item.title}</div>
                      <p className="hj-dc-layer__desc">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=900&q=80"
              alt="Juniper data center networking"
              className="hj-dc__img"
            />
          </div>
        </div>
      </section>

      {/* ── Wireless ── */}
      <section className="hj-wireless">
        <div className="hj-shell">
          <div className="hj-wireless__inner">
            <img
              src="https://images.unsplash.com/photo-1606868306217-dbf5046868d2?auto=format&fit=crop&w=900&q=80"
              alt="Juniper Mist wireless infrastructure"
              className="hj-wireless__img"
            />
            <div>
              <div className="hj-tag hj-tag--light-green"><span className="hj-dot" /> Wireless Infrastructure</div>
              <h2 className="hj-title hj-wireless__title" style={{ color:"var(--hj-text)" }}>Mist Wi-Fi — Cloud-Native. AI-Operated. Everywhere.</h2>
              <p className="hj-wireless__body">
                Juniper Mist access points integrate Wi-Fi 6E and Virtual BLE in a single hardware platform — delivering multi-gigabit wireless performance alongside meter-level indoor location services for asset tracking and campus navigation, all managed through Mist Cloud without any on-premises wireless controller infrastructure.
              </p>
              <div className="hj-wireless__specs">
                {wirelessSpecs.map(s => (
                  <div key={s.val} className="hj-wspec">
                    <div className="hj-wspec__val">{s.val}</div>
                    <div className="hj-wspec__label">{s.label}</div>
                  </div>
                ))}
              </div>
              <div className="hj-wireless__features">
                {wirelessFeatures.map(f => <div key={f} className="hj-wireless-feat">{f}</div>)}
              </div>
              <button className="hj-btn hj-btn--dark-blue" onClick={() => navigate("/contact")}>
                Design My Wireless <ArrowIcon />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── AI Operations ── */}
      <section className="hj-aiops">
        <div className="hj-shell">
          <div className="hj-aiops__inner">
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=80"
              alt="Juniper Marvis AI network operations"
              className="hj-aiops__img"
            />
            <div>
              <div className="hj-tag hj-tag--green"><span className="hj-dot" /> AI Operations</div>
              <h2 className="hj-title hj-aiops__title">Marvis — Ask, Detect, Resolve. Automatically.</h2>
              <p className="hj-aiops__body">
                Marvis AI goes beyond monitoring — it operates the network. By continuously measuring Service Level Experience metrics from the client perspective, Marvis identifies performance degradation, finds root causes, and either automatically remediates or surfaces specific actionable recommendations for engineers. The result: network teams that previously spent 60% of their time troubleshooting spend that time on strategic work instead.
              </p>
              <div className="hj-aiops__capabilities">
                {aiopsCaps.map(cap => (
                  <div key={cap.title} className="hj-aiops-cap">
                    <span className="hj-aiops-cap__icon">{cap.icon}</span>
                    <div>
                      <div className="hj-aiops-cap__title">{cap.title}</div>
                      <p className="hj-aiops-cap__desc">{cap.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="hj-btn hj-btn--green" onClick={() => navigate("/contact")}>
                  See Marvis in Action <ArrowIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Deployment Journey ── */}
      <section className="hj-deploy hj-blueprint">
        <div className="hj-shell">
          <div className="hj-deploy__head">
            <div className="hj-tag hj-tag--blue" style={{ margin:"0 auto 1rem" }}><span className="hj-dot" /> Deployment Journey</div>
            <h2 className="hj-title" style={{ color:"#fff" }}>From Design to Day-2 Operations — Without Complexity</h2>
            <p className="hj-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>ALG Data Guard follows Juniper's validated deployment methodology — from network discovery through Mist onboarding through ongoing AI-managed operations.</p>
          </div>
          <div className="hj-deploy__steps">
            {deploySteps.map(step => (
              <div key={step.num} className="hj-deploy-step">
                <div className="hj-deploy-step__num">{step.num}</div>
                <span className="hj-deploy-step__icon">{step.icon}</span>
                <h3 className="hj-deploy-step__title">{step.title}</h3>
                <p className="hj-deploy-step__desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="hj-industries">
        <div className="hj-shell">
          <div className="hj-industries__head">
            <div className="hj-tag hj-tag--light-blue"><span className="hj-dot" /> Industries</div>
            <h2 className="hj-title">Juniper Mist in Every Vertical</h2>
            <p className="hj-sub" style={{ color:"var(--hj-muted)", margin:"0 auto", maxWidth:"560px" }}>Juniper Mist and the AI-native networking platform serve organizations across every industry — with validated reference architectures and compliance-aligned configurations.</p>
          </div>
          <div className="hj-industries__grid">
            {industries.map(ind => (
              <div key={ind.name} className="hj-ind-card">
                <span className="hj-ind-card__icon">{ind.icon}</span>
                <h3 className="hj-ind-card__name">{ind.name}</h3>
                <p className="hj-ind-card__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Metrics ── */}
      <section className="hj-metrics" ref={metricsRef}>
        <div className="hj-shell">
          <div className="hj-metrics__inner">
            <div><div className="hj-metric__val">{c1}%</div><div className="hj-metric__label">Reduction in MTTR with Marvis</div></div>
            <div><div className="hj-metric__val">{c2}+</div><div className="hj-metric__label">Countries with Mist Deployments</div></div>
            <div><div className="hj-metric__val">{c3}M+</div><div className="hj-metric__label">Client Sessions Analyzed Daily</div></div>
            <div><div className="hj-metric__val">{c4}%</div><div className="hj-metric__label">Fewer Help Desk Tickets</div></div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="hj-faq">
        <div className="hj-shell">
          <div className="hj-faq__head">
            <div className="hj-tag hj-tag--blue" style={{ margin:"0 auto 1rem" }}><span className="hj-dot" /> FAQs</div>
            <h2 className="hj-title">Juniper Mist Questions, Answered</h2>
            <p className="hj-sub" style={{ margin:"0 auto", maxWidth:"560px" }}>Expert answers from ALG Data Guard's Juniper-certified engineers about Mist AI, Marvis, EVPN-VXLAN, and enterprise networking deployments.</p>
          </div>
          <div className="hj-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`hj-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="hj-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="hj-faq-item__q">{faq.q}</span>
                  <span className="hj-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="hj-faq-item__answer"><div className="hj-faq-item__answer-inner">{faq.a}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="hj-cta hj-blueprint">
        <div className="hj-cta__inner">
          <div className="hj-tag hj-tag--green" style={{ margin:"0 auto 1.2rem" }}><span className="hj-dot" /> Get Started</div>
          <h2 className="hj-cta__title">
            Build the Network<br />That <span>Operates Itself.</span>
          </h2>
          <p className="hj-cta__sub">Whether you're deploying Juniper Mist for a single campus, implementing EVPN-VXLAN data center fabric, or replacing an aging campus switching infrastructure — ALG Data Guard's Juniper-certified engineers design and deliver AI-native networks that reduce operational burden while improving user experience.</p>
          <div className="hj-cta__actions">
            <button className="hj-btn hj-btn--green" onClick={() => navigate("/contact")}>
              Start My Juniper Project <ArrowIcon />
            </button>
            <button className="hj-btn hj-btn--outline-blue" onClick={() => navigate("/contact")}>
              Request Network Assessment
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
